import { useEffect, useState, useCallback } from "react";
import { Megaphone, Clock, CheckCircle2, Radio, XCircle, ChevronDown, Music, Mail, Phone, MessageSquare, DollarSign, FileAudio } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const STATUS_OPTIONS = ["pending", "confirmed", "scheduled", "live", "completed", "cancelled"];

const STATUS_STYLES = {
  pending:   { bg: "bg-yellow-500/10", text: "text-yellow-400", label: "Pending" },
  confirmed: { bg: "bg-blue-500/10", text: "text-blue-400", label: "Confirmed" },
  scheduled: { bg: "bg-purple-500/10", text: "text-purple-400", label: "Scheduled" },
  live:      { bg: "bg-green-500/10", text: "text-green-400", label: "Live" },
  completed: { bg: "bg-gray-500/10", text: "text-gray-400", label: "Completed" },
  cancelled: { bg: "bg-red-500/10", text: "text-red-400", label: "Cancelled" },
};

const PAYMENT_STYLES = {
  pending: { bg: "bg-yellow-500/10", text: "text-yellow-400", label: "Unpaid" },
  created: { bg: "bg-orange-500/10", text: "text-orange-400", label: "Awaiting Payment" },
  paid:    { bg: "bg-green-500/10", text: "text-green-400", label: "Paid" },
};

const AdminAdBookingsTab = ({ authToken, onMessage }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState("all");

  const fetchBookings = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ad-bookings`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBookings(data.bookings || []);
      }
    } catch (e) {
      console.error("Failed to fetch ad bookings:", e);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (bookingId, newStatus) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ad-bookings/${bookingId}/status?status=${newStatus}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        setBookings(prev => prev.map(b => b.booking_id === bookingId ? { ...b, status: newStatus } : b));
        if (onMessage) onMessage(`Ad booking status updated to ${newStatus}`);
      }
    } catch (e) {
      console.error("Status update failed:", e);
    }
  };

  const filtered = filter === "all" ? bookings : bookings.filter(b => b.status === filter);

  // Summary counts
  const counts = {};
  for (const b of bookings) {
    counts[b.status] = (counts[b.status] || 0) + 1;
  }
  const totalRevenue = bookings
    .filter(b => b.payment_status === "paid")
    .reduce((sum, b) => sum + parseFloat(b.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Megaphone className="w-8 h-8 text-purple-400 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-ad-bookings">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total Bookings", value: bookings.length, color: "text-white" },
          { label: "Pending", value: counts.pending || 0, color: "text-yellow-400" },
          { label: "Live / Scheduled", value: (counts.live || 0) + (counts.scheduled || 0), color: "text-green-400" },
          { label: "Completed", value: counts.completed || 0, color: "text-gray-400" },
          { label: "Revenue (Paid)", value: `$${totalRevenue.toFixed(2)}`, color: "text-emerald-400" },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-xl bg-white/5 border border-white/5 p-4 text-center">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-white/40 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setFilter("all")}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${filter === "all" ? "bg-purple-500/20 text-purple-400" : "bg-white/5 text-white/50 hover:text-white"}`}
          data-testid="filter-all"
        >
          All ({bookings.length})
        </button>
        {STATUS_OPTIONS.map(s => {
          const st = STATUS_STYLES[s];
          const count = counts[s] || 0;
          return (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${filter === s ? `${st.bg} ${st.text}` : "bg-white/5 text-white/50 hover:text-white"}`}
              data-testid={`filter-${s}`}
            >
              {st.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Bookings List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{filter === "all" ? "No ad bookings yet" : `No ${filter} bookings`}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(b => {
            const st = STATUS_STYLES[b.status] || STATUS_STYLES.pending;
            const ps = PAYMENT_STYLES[b.payment_status] || PAYMENT_STYLES.pending;
            const expanded = expandedId === b.booking_id;

            return (
              <div
                key={b.booking_id}
                className="rounded-xl border border-white/5 bg-white/[0.02] overflow-hidden"
                data-testid={`booking-row-${b.booking_id}`}
              >
                {/* Main row */}
                <div
                  className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/[0.02] transition"
                  onClick={() => setExpandedId(expanded ? null : b.booking_id)}
                >
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                    <Megaphone className="w-5 h-5 text-purple-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{b.business_name}</p>
                    <p className="text-white/40 text-xs">{b.plan_name} — ${b.amount}/mo · {b.email}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-semibold ${ps.bg} ${ps.text}`}>{ps.label}</span>
                    <span className={`px-2 py-1 rounded-full text-[10px] font-semibold ${st.bg} ${st.text}`}>{st.label}</span>
                    <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${expanded ? "rotate-180" : ""}`} />
                  </div>
                </div>

                {/* Expanded details */}
                {expanded && (
                  <div className="border-t border-white/5 px-4 py-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-white/60">
                        <Mail className="w-3.5 h-3.5 text-white/30" /> {b.email}
                      </div>
                      {b.phone && (
                        <div className="flex items-center gap-2 text-white/60">
                          <Phone className="w-3.5 h-3.5 text-white/30" /> {b.phone}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-white/60">
                        <DollarSign className="w-3.5 h-3.5 text-white/30" /> {b.plan_name} Plan — ${b.amount}/mo
                      </div>
                      <div className="flex items-center gap-2 text-white/60">
                        <Clock className="w-3.5 h-3.5 text-white/30" /> {new Date(b.created_at).toLocaleDateString()} {new Date(b.created_at).toLocaleTimeString()}
                      </div>
                      <div className="flex items-center gap-2 text-white/60">
                        <FileAudio className="w-3.5 h-3.5 text-white/30" /> {b.audio_url ? `Audio: ${b.audio_filename || "Uploaded"}` : "No audio uploaded"}
                      </div>
                      {b.message && (
                        <div className="flex items-start gap-2 text-white/60 md:col-span-2">
                          <MessageSquare className="w-3.5 h-3.5 text-white/30 mt-0.5" /> {b.message}
                        </div>
                      )}
                    </div>

                    {/* Status controls */}
                    <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                      <span className="text-white/30 text-xs mr-2">Set status:</span>
                      {STATUS_OPTIONS.map(s => {
                        const sst = STATUS_STYLES[s];
                        const active = b.status === s;
                        return (
                          <button
                            key={s}
                            onClick={(e) => { e.stopPropagation(); if (!active) updateStatus(b.booking_id, s); }}
                            className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition ${active ? `${sst.bg} ${sst.text} ring-1 ring-current` : "bg-white/5 text-white/40 hover:text-white hover:bg-white/10"}`}
                            data-testid={`status-btn-${s}-${b.booking_id}`}
                          >
                            {sst.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminAdBookingsTab;
